/**
 * Animation Renderer
 *
 * Renders animation frames to image elements using CSS transforms.
 * Supports breathing, parallax, and sway animation types with
 * configurable intensity levels and loop/one-shot modes.
 */

import {
    GeminiAnimationResponse,
    AnimationType,
    AnimationIntensity,
    TrackedImage,
} from './types';

/**
 * Intensity multipliers for animation effects
 */
const INTENSITY_MULTIPLIERS: Record<AnimationIntensity, number> = {
    subtle: 0.3,
    medium: 0.6,
    strong: 1.0,
};

/**
 * Animation preset configurations
 */
const ANIMATION_PRESETS: Record<
    Exclude<AnimationType, 'auto'>,
    {
        translateX: number;
        translateY: number;
        scale: number;
        rotate: number;
        duration: number;
    }
> = {
    breathing: {
        translateX: 0,
        translateY: 0,
        scale: 0.02,
        rotate: 0,
        duration: 3000,
    },
    parallax: {
        translateX: 5,
        translateY: 3,
        scale: 0.01,
        rotate: 0,
        duration: 4000,
    },
    sway: {
        translateX: 3,
        translateY: 0,
        scale: 0,
        rotate: 2,
        duration: 2500,
    },
};

/**
 * Animation renderer configuration
 */
export interface AnimationRendererConfig {
    /** Target frame rate (default: 60) */
    frameRate?: number;
    /** Enable debug logging (default: false) */
    debug?: boolean;
}

/**
 * Animation state for a single image
 */
interface AnimationState {
    trackedImage: TrackedImage;
    animationData: GeminiAnimationResponse;
    currentFrame: number;
    animationFrameId: number | null;
    startTime: number;
    isPaused: boolean;
    intensity: AnimationIntensity;
    loop: boolean;
    onComplete?: () => void;
}

/**
 * AnimationRenderer class
 *
 * Handles rendering animation frames to DOM elements using
 * requestAnimationFrame for smooth playback.
 */
export class AnimationRenderer {
    private config: Required<AnimationRendererConfig>;
    private animations: Map<string, AnimationState> = new Map();

    constructor(config: AnimationRendererConfig = {}) {
        this.config = {
            frameRate: config.frameRate ?? 60,
            debug: config.debug ?? false,
        };
    }

    /**
     * Start animation playback for an image
     *
     * @param trackedImage - The tracked image to animate
     * @param animationData - Animation data from Gemini API
     * @param options - Animation options
     */
    play(
        trackedImage: TrackedImage,
        animationData: GeminiAnimationResponse,
        options: {
            intensity?: AnimationIntensity;
            loop?: boolean;
            onComplete?: () => void;
        } = {}
    ): void {
        const { id, element } = trackedImage;

        // Stop any existing animation
        this.stop(id);

        // Prepare element for animation
        element.style.willChange = 'transform';
        element.style.transformOrigin = 'center center';

        const state: AnimationState = {
            trackedImage,
            animationData,
            currentFrame: 0,
            animationFrameId: null,
            startTime: performance.now(),
            isPaused: false,
            intensity: options.intensity ?? trackedImage.config.intensity,
            loop: options.loop ?? trackedImage.config.loop,
            onComplete: options.onComplete,
        };

        this.animations.set(id, state);

        if (this.config.debug) {
            console.log('[AnimationRenderer] Starting animation for:', id);
        }

        this.renderLoop(id);
    }

    /**
     * Pause animation for an image
     */
    pause(imageId: string): void {
        const state = this.animations.get(imageId);
        if (state && !state.isPaused) {
            state.isPaused = true;
            if (state.animationFrameId !== null) {
                cancelAnimationFrame(state.animationFrameId);
                state.animationFrameId = null;
            }

            if (this.config.debug) {
                console.log('[AnimationRenderer] Paused animation for:', imageId);
            }
        }
    }

    /**
     * Resume animation for an image
     */
    resume(imageId: string): void {
        const state = this.animations.get(imageId);
        if (state && state.isPaused) {
            state.isPaused = false;
            state.startTime = performance.now() - this.getElapsedTime(state);
            this.renderLoop(imageId);

            if (this.config.debug) {
                console.log('[AnimationRenderer] Resumed animation for:', imageId);
            }
        }
    }

    /**
     * Stop animation for an image and reset transforms
     */
    stop(imageId: string): void {
        const state = this.animations.get(imageId);
        if (state) {
            if (state.animationFrameId !== null) {
                cancelAnimationFrame(state.animationFrameId);
            }

            // Reset element styles
            state.trackedImage.element.style.transform = '';
            state.trackedImage.element.style.willChange = '';

            this.animations.delete(imageId);

            if (this.config.debug) {
                console.log('[AnimationRenderer] Stopped animation for:', imageId);
            }
        }
    }

    /**
     * Check if an image is currently animating
     */
    isPlaying(imageId: string): boolean {
        const state = this.animations.get(imageId);
        return state !== undefined && !state.isPaused;
    }

    /**
     * Get all currently animating image IDs
     */
    getActiveAnimations(): string[] {
        return Array.from(this.animations.keys());
    }

    /**
     * Stop all animations and clean up
     */
    destroy(): void {
        for (const imageId of this.animations.keys()) {
            this.stop(imageId);
        }
        this.animations.clear();
    }

    /**
     * Main render loop using requestAnimationFrame
     */
    private renderLoop(imageId: string): void {
        const state = this.animations.get(imageId);
        if (!state || state.isPaused) {
            return;
        }

        const elapsed = performance.now() - state.startTime;
        const duration = state.animationData.duration || 3000;
        const progress = (elapsed % duration) / duration;

        // Check if animation should complete (non-looping)
        if (!state.loop && elapsed >= duration) {
            this.stop(imageId);
            if (state.onComplete) {
                state.onComplete();
            }
            return;
        }

        // Render the current frame
        this.renderFrame(state, progress);

        // Schedule next frame
        state.animationFrameId = requestAnimationFrame(() => {
            this.renderLoop(imageId);
        });
    }

    /**
     * Render a single animation frame
     */
    private renderFrame(state: AnimationState, progress: number): void {
        const { trackedImage, animationData, intensity } = state;
        const multiplier = INTENSITY_MULTIPLIERS[intensity];

        let transform: { translateX: number; translateY: number; scale: number; rotate: number };

        // Use animation data if available, otherwise generate from type
        if (animationData.frames && animationData.frames.length > 0) {
            const frameIndex = Math.floor(progress * animationData.frames.length);
            const frame = animationData.frames[Math.min(frameIndex, animationData.frames.length - 1)];
            // Handle partial transform data (API might only return scale)
            const frameTransform = frame?.transform || {};
            transform = {
                translateX: frameTransform.translateX ?? 0,
                translateY: frameTransform.translateY ?? 0,
                scale: frameTransform.scale ?? 1,
                rotate: frameTransform.rotate ?? 0,
            };
            // Normalize scale: API returns absolute scale (e.g., 1.0075), we need delta
            if (transform.scale !== 0 && Math.abs(transform.scale) > 0.5) {
                transform.scale = transform.scale - 1; // Convert 1.0075 to 0.0075
            }
        } else {
            // Generate animation from detected type
            const animType = animationData.detectedType || 'breathing';
            if (animType !== 'auto') {
                transform = this.generateTransformFromType(animType, progress);
            } else {
                transform = this.generateTransformFromType('breathing', progress);
            }
        }

        // Apply intensity multiplier
        const translateX = transform.translateX * multiplier;
        const translateY = transform.translateY * multiplier;
        const scale = 1 + transform.scale * multiplier;
        const rotate = transform.rotate * multiplier;

        // Apply CSS transform
        trackedImage.element.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale}) rotate(${rotate}deg)`;
    }

    /**
     * Generate transform values for a given animation type
     */
    private generateTransformFromType(
        type: Exclude<AnimationType, 'auto'>,
        progress: number
    ): { translateX: number; translateY: number; scale: number; rotate: number } {
        const preset = ANIMATION_PRESETS[type] || ANIMATION_PRESETS.breathing;

        // Use sine wave for smooth oscillation
        const oscillation = Math.sin(progress * Math.PI * 2);

        return {
            translateX: preset.translateX * oscillation,
            translateY: preset.translateY * oscillation,
            scale: preset.scale * oscillation,
            rotate: preset.rotate * oscillation,
        };
    }

    /**
     * Calculate elapsed time for a paused animation
     */
    private getElapsedTime(state: AnimationState): number {
        const duration = state.animationData.duration || 3000;
        return (state.currentFrame / state.animationData.frameRate) * 1000 % duration;
    }
}
